import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Chip, LinearProgress, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useGetFilesQuery } from 'store/services/documentStorage/googleCloudStorage';
import type { DocumentEditorProps } from 'types/documentEditing';
import { useDocumentSession } from 'utils/hooks/documentEditing/useDocumentSession';
import { useDocumentExpiry } from 'utils/hooks/documentEditing/useDocumentExpiry';
import { useBeforeUnload } from 'utils/hooks/useBeforeUnload';
import { useNavigationBlocker, useNavigationBlockerState } from 'contexts/NavigationBlockerContext';
import { EditorHeader, EditorRoot } from './DocumentEditor.styles';
import { DocumentFrame } from './components/DocumentFrame';
import { DocumentEditorConfirmation } from './components/DocumentEditorConfirmation';

export const DocumentEditor = ({ args, origin, onClose, initialName, closeNavigates = false, closeRequestRef }: DocumentEditorProps) => {
  const { t } = useTranslation();
  const { session, errorKey, takeLaunchSession, reopen } = useDocumentSession(args, origin);
  const { expired, expiring } = useDocumentExpiry(session?.accessTokenTtl);
  const { data } = useGetFilesQuery({ ids: [args.fileId] }, { skip: !session, refetchOnMountOrArgChange: true });
  const [pendingAction, setPendingAction] = useState<'close' | 'reopen' | null>(null);
  const [leaving, setLeaving] = useState(false);
  const canWrite = Boolean(session?.canEdit || session?.canReview);
  const blocker = useNavigationBlocker(canWrite && !leaving, { priority: 100, group: 'document' });
  const { navigationPending } = useNavigationBlockerState();
  const confirmationOpen = pendingAction !== null || blocker.state === 'blocked';
  const name = data?.files.find((file) => file.id === args.fileId)?.name ?? initialName ?? t('user.documentEditor.title');
  useBeforeUnload({ when: canWrite && !leaving });

  useEffect(() => {
    if (leaving) onClose();
  }, [leaving, onClose]);

  const performAction = useCallback(
    (action: 'close' | 'reopen') => {
      if (action === 'close') setLeaving(true);
      else reopen();
    },
    [reopen]
  );

  const requestAction = useCallback(
    (action: 'close' | 'reopen') => {
      if (pendingAction || navigationPending || leaving) return;
      if (action === 'close' && closeNavigates) onClose();
      else if (canWrite) setPendingAction(action);
      else performAction(action);
    },
    [pendingAction, navigationPending, leaving, closeNavigates, onClose, canWrite, performAction]
  );

  useEffect(() => {
    if (!closeRequestRef) return;
    closeRequestRef.current = () => requestAction('close');
    return () => {
      closeRequestRef.current = null;
    };
  }, [closeRequestRef, requestAction]);

  return (
    <EditorRoot data-testid="document-editor">
      <Helmet>
        <title>{closeNavigates ? name : `${name} | Flowio`}</title>
        <meta name="referrer" content="no-referrer" />
      </Helmet>
      <EditorHeader>
        <Typography id="document-editor-title" component="h1" variant="subtitle1" noWrap title={name} sx={{ flex: 1, minWidth: 100 }}>
          {name}
        </Typography>
        {session && <Chip size="small" label={t(`user.documentEditor.modes.${session.mode}`)} />}
        {(session || errorKey) && (
          <Button disabled={confirmationOpen || navigationPending || leaving} onClick={() => requestAction('reopen')}>
            {t('user.documentEditor.reopen')}
          </Button>
        )}
        <Button disabled={confirmationOpen || navigationPending || leaving} onClick={() => requestAction('close')}>
          {t('common.close')}
        </Button>
      </EditorHeader>
      {expired && <Alert severity="error">{t('user.documentEditor.expired')}</Alert>}
      {expiring && <Alert severity="warning">{t('user.documentEditor.expiring')}</Alert>}
      {errorKey && <Alert severity="error">{t(errorKey)}</Alert>}
      {!session && !errorKey && <LinearProgress aria-label={t('user.documentEditor.loading')} />}
      {session && (
        <DocumentFrame
          key={session.sessionId}
          session={session}
          origin={origin}
          title={name}
          takeLaunchSession={takeLaunchSession}
          overlayOpen={confirmationOpen || navigationPending}
        />
      )}
      <DocumentEditorConfirmation
        open={confirmationOpen}
        onConfirm={() => {
          if (blocker.state === 'blocked') blocker.proceed();
          else if (pendingAction) performAction(pendingAction);
          setPendingAction(null);
        }}
        onCancel={() => {
          if (blocker.state === 'blocked') blocker.reset();
          setPendingAction(null);
        }}
      />
    </EditorRoot>
  );
};
